import { Spinner } from '@fluentui/react-components';
import { getCoprincipalMailboxInfoForMailboxInfo } from 'owa-account-source-list-store';
import { logCoreUsage } from 'owa-analytics';
import { DrawerPane, isDrawerPaneVisible } from 'owa-drawer';
import { getIsDarkTheme } from 'owa-fabric-theme';
import type { OwaHeaderPaneProps } from 'owa-header-types';
import { OWAIframe } from 'owa-iframe';
import loc, { getCurrentLanguageFromCurrentLocale } from 'owa-localize';
import { observer } from 'owa-mobx-react';
import { getModuleContextMailboxInfo } from 'owa-module-context-mailboxinfo';
import { useState } from 'owa-react';
import { useCallback, useEffect, useRef, type SyntheticEvent, type FunctionComponent } from 'react';
import RewardsFallback from './RewardsFallback';
import getRewardsExpectedAnid from '../utils/getRewardsExpectedAnid';
import waitForFlyoutReady from '../utils/waitForFlyoutReady';
import {
    loadingRewardsText,
    microsoftRewardsTitle,
    rewardsPaneHeaderText,
} from './RewardsPane.locstring.json';
import {
    header,
    headerTitle,
    iframeContainer,
    loadingCover,
    logo,
    titleText,
    bodyContainer,
} from './RewardsPane.scss';

const getRewardsIframeUrl = (isDarkMode: boolean, owaAnid: string) => {
    let returnUrl = `https://www.bing.com/rewards/panelflyout?partnerId=OutlookOwa&isDarkMode=${
        isDarkMode ? 1 : 0
    }&lat=1&lang=${encodeURIComponent(getCurrentLanguageFromCurrentLocale())}`;
    // unresolved we omit it; the server treats a missing value as a mismatch and fails safe.
    if (owaAnid) {
        returnUrl += `&owaAnid=${encodeURIComponent(owaAnid)}`;
    }
    return `https://www.bing.com/fd/auth/signin?action=interactive&provider=windows_live_id&return_url=${encodeURIComponent(
        returnUrl
    )}`;
};
const rewardsIframeStyle = { border: 0, display: 'block', height: '100%', width: '100%' };

const rewardsPaneTitle = () => {
    return (
        <span className={headerTitle}>
            <svg
                className={logo}
                width="16"
                height="16"
                viewBox="0 0 21 21"
                aria-hidden="true"
                xmlns="http://www.w3.org/2000/svg"
            >
                <rect x="1" y="1" width="9" height="9" fill="#f25022" />
                <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
                <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
                <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
            </svg>
            <span className={titleText}>{loc(microsoftRewardsTitle)}</span>
        </span>
    );
};

const defaultExport: FunctionComponent<OwaHeaderPaneProps> = observer(function RewardsPane(
    props: OwaHeaderPaneProps
) {
    const isVisible = isDrawerPaneVisible(props.paneId);
    // 'loading' while we resolve the OWA ANID and wait for the flyout's ready signal, 'ready' once
    // the flyout reports a matching identity, 'blocked' if the signal never arrives (cookies
    // blocked), and 'mismatch' if the flyout reports the OWA account does not match the Bing
    // session.
    const [status, setStatus] = useState<'loading' | 'ready' | 'blocked' | 'mismatch'>('loading');
    // The flyout iframe URL, built with the OWA account's ANID once it resolves. Undefined until
    // then so the iframe is never constructed (and the wrong account never loads) before the
    // identity is known.
    const [iframeUrl, setIframeUrl] = useState<string | undefined>(undefined);

    // Holds the Rewards iframe's contentWindow once it loads, so detection can verify the
    // 'ready' postMessage actually came from our iframe and not another same-origin frame.
    const flyoutWindowRef = useRef<Window | null>(null);
    const handleIframeLoad = useCallback((event: SyntheticEvent<HTMLIFrameElement>) => {
        flyoutWindowRef.current = event.currentTarget.contentWindow;
    }, []);

    // The pane stays mounted while dismissed (preserveContentOnDismiss). Reset on dismiss so the
    // identity guard re-validates on the next open; otherwise a Bing-session change while dismissed
    // could expose the retained iframe unguarded.
    const [prevIsVisible, setPrevIsVisible] = useState(false);
    if (prevIsVisible !== isVisible) {
        setPrevIsVisible(isVisible);
        if (!isVisible) {
            setStatus('loading');
            setIframeUrl(undefined);
        }
    }

    useEffect(() => {
        if (!isVisible || status !== 'loading') {
            return;
        }
        // Drop any contentWindow retained from a previous open before re-validating.
        flyoutWindowRef.current = null;
        let cancelled = false;
        const moduleMailboxInfo = getModuleContextMailboxInfo();
        const coprincipalMailboxInfo = getCoprincipalMailboxInfoForMailboxInfo(moduleMailboxInfo);
        const telemetryMailboxInfo = coprincipalMailboxInfo ?? moduleMailboxInfo;
        const isDarkMode = getIsDarkTheme();
        void (async () => {
            const owaAnid = coprincipalMailboxInfo
                ? await getRewardsExpectedAnid(coprincipalMailboxInfo)
                : '';
            if (cancelled) {
                return;
            }
            const readyPromise = waitForFlyoutReady(() => flyoutWindowRef.current);
            setIframeUrl(getRewardsIframeUrl(isDarkMode, owaAnid));
            const flyoutResult = await readyPromise;
            if (cancelled) {
                return;
            }
            let nextStatus: 'ready' | 'blocked' | 'mismatch';
            let outcome: 'flyout' | 'fallback' | 'mismatch';
            if (flyoutResult.blocked) {
                nextStatus = 'blocked';
                outcome = 'fallback';
            } else if (flyoutResult.anidMatch !== true) {
                nextStatus = 'mismatch';
                outcome = 'mismatch';
            } else {
                nextStatus = 'ready';
                outcome = 'flyout';
            }
            logCoreUsage(
                'RewardsPaneOpened',
                telemetryMailboxInfo,
                { outcome },
                { isScorecardEvent: true }
            );
            setStatus(nextStatus);
        })();
        return () => {
            cancelled = true;
        };
    }, [isVisible, status]);

    return (
        <DrawerPane
            paneId={props.paneId}
            bodyClassName={bodyContainer}
            headerText={rewardsPaneTitle()}
            headerClassName={header}
            noPadding={true}
            preserveContentOnDismiss={false}
            role={'complementary'}
            width={380}
        >
            {status === 'blocked' ? (
                <RewardsFallback variant="cookiesBlocked" />
            ) : status === 'mismatch' ? (
                <RewardsFallback variant="error" />
            ) : (
                <div className={iframeContainer}>
                    {iframeUrl !== undefined && (
                        <OWAIframe
                            isVisible={isVisible}
                            scenario="RewardsPane"
                            src={iframeUrl}
                            title={loc(rewardsPaneHeaderText)}
                            style={rewardsIframeStyle}
                            onLoad={handleIframeLoad}
                        />
                    )}
                    {status === 'loading' && (
                        <div className={loadingCover}>
                            <Spinner
                                size="medium"
                                labelPosition="below"
                                label={loc(loadingRewardsText)}
                            />
                        </div>
                    )}
                </div>
            )}
        </DrawerPane>
    );
},
'RewardsPane');

export default defaultExport;
